import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { testimonials } from '@/data/testimonials'

// Real site section 6 background: rgba(245,209,198,0.65) — blush/warm pink tint
// Padding: 75px top / 75px bottom / 40px horizontal
// Text color: #5c5346 (warm dark brown) per site tokens

export function Testimonials() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith('en') ? 'en' : 'es'
  const [current, setCurrent] = useState(0)

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % testimonials.length)
  }, [])

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }, [])

  useEffect(() => {
    const timer = setInterval(next, 5000)
    return () => clearInterval(timer)
  }, [next])

  const testimonial = testimonials[current]
  const text = locale === 'es' ? testimonial.textEs : testimonial.textEn

  return (
    <section
      style={{
        backgroundColor: 'rgba(245,209,198,0.65)',
        paddingTop: '75px',
        paddingBottom: '75px',
        paddingLeft: '40px',
        paddingRight: '40px',
      }}
    >
      <div className="mx-auto max-w-4xl">
        {/* Section heading — uppercase Montserrat, navy */}
        <AnimatedSection className="mb-10 text-center">
          <h2
            className="text-3xl font-bold uppercase tracking-wide"
            style={{ color: '#0F2966' }}
          >
            {t('testimonials.title')}
          </h2>
        </AnimatedSection>

        {/* Carousel area */}
        <div className="relative px-10 md:px-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="text-center"
            >
              {/* 5-star rating row */}
              <div className="mb-6 flex items-center justify-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-5 w-5"
                    style={{
                      fill: i < testimonial.rating ? '#0F2966' : 'rgba(15,41,102,0.2)',
                      color: i < testimonial.rating ? '#0F2966' : 'rgba(15,41,102,0.2)',
                    }}
                  />
                ))}
              </div>

              {/* Quote text — warm dark brown #5c5346 matching real site testimonial_text token */}
              <blockquote
                className="text-xl leading-relaxed md:text-2xl"
                style={{ color: '#5c5346', fontFamily: 'Montserrat, sans-serif' }}
              >
                &ldquo;{text}&rdquo;
              </blockquote>

              {/* Attribution */}
              <div className="mt-6">
                <p className="font-semibold" style={{ color: '#0F2966' }}>
                  {testimonial.name}
                </p>
                <p className="text-sm" style={{ color: '#5c5346' }}>
                  {testimonial.origin}
                </p>
                <p
                  className="mt-1 text-xs capitalize"
                  style={{ color: '#5c5346', opacity: 0.7 }}
                >
                  {testimonial.source}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-2 transition-colors hover:bg-white/40"
            style={{ color: '#5c5346' }}
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-2 transition-colors hover:bg-white/40"
            style={{ color: '#5c5346' }}
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="mt-8 flex items-center justify-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className="rounded-full transition-all"
              style={{
                width: i === current ? '24px' : '8px',
                height: '8px',
                backgroundColor: i === current ? '#0F2966' : 'rgba(15,41,102,0.3)',
              }}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
